import { useEffect, useState } from "react";
import { Mail, Loader2, X } from "lucide-react";
import { api, errText } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";

export function ChangeEmailCard({ user }) {
  const { t } = useLanguage();
  const [info, setInfo] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/auth/me/email-change").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/me/email-change", { new_email: newEmail.trim(), password: pw });
      toast.success(`${t("ce_sent")} ${data.new_email}`);
      setNewEmail(""); setPw(""); load();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await api.delete("/auth/me/email-change"); toast.success(t("ce_cancelled")); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  if (!info) return null;
  return (
    <div className="max-w-xl bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="change-email-card">
      <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><Mail size={18} className="text-[#F5551E]" /> {t("ce_title")}</h3>
      <p className="text-sm text-slate-500 mt-0.5">{t("ce_current")} <span className="font-semibold text-[#12234A]" data-testid="change-email-current">{user?.email}</span></p>
      {!info.can_change ? (
        <p className="mt-3 text-sm text-slate-500 bg-slate-50 rounded-xl px-3 py-2" data-testid="change-email-google-note">{t("ce_google")}</p>
      ) : (
        <>
          {info.pending && (
            <div className="mt-3 flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-3 py-2" data-testid="change-email-pending">
              <span className="flex-1">{t("ce_pending")} <b>{info.pending.new_email}</b>. {t("ce_pending2")}</span>
              <button onClick={cancel} data-testid="change-email-cancel" title="Cancel" className="h-7 w-7 rounded-lg hover:bg-amber-100 flex items-center justify-center"><X size={14} /></button>
            </div>
          )}
          <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("ce_new")}</Label>
              <Input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="change-email-new" className="mt-1 h-10 rounded-xl" />
            </div>
            <div>
              <Label>{t("ce_pw")}</Label>
              <Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" data-testid="change-email-password" className="mt-1 h-10 rounded-xl" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-3">
              <Button type="submit" disabled={busy} data-testid="change-email-submit" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
                {busy ? <Loader2 size={16} className="animate-spin" /> : t("ce_send")}
              </Button>
              <span className="text-xs text-slate-400">{t("ce_hint")}</span>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
